import { makeAutoObservable } from "mobx";
import { BoundingBox } from "../../../layout/LabsLayout/Results/Canvas/types";
import { isFibo2App } from "../../../utils";

interface ResultState {
	hoveredObjectId: string | null;
	selectedObjectId: string | null;
	openNodes: Set<string>;
	showAllObjects: boolean;
	isCanvasHovered: boolean;
	isEmbedDrawActive: boolean;
	embedBbox: BoundingBox | null;
	embedFile: File | null;
	embedSourceObjectId: string | null;
}

export interface IStructuredPromptStore {
	hoveredObjectId: string | null;
	selectedObjectId: string | null;
	showAllObjects: boolean;
	isCanvasHovered: boolean;
	/** FIBO 2 "Embed Image": free-draw mode is armed / a drag is in progress. */
	isEmbedDrawActive: boolean;
	/** The committed embed area, in % of the displayed image. */
	embedBbox: BoundingBox | null;
	/** The image to embed into that area on the next Refine. */
	embedFile: File | null;
	/** Path key of the object "Embed here" seeded the area from; null for drawn areas. */
	embedSourceObjectId: string | null;
	setHoveredObjectId: (id: string | null) => void;
	setSelectedObjectId: (id: string | null) => void;
	setShowAllObjects: (show: boolean) => void;
	toggleShowAllObjects: () => void;
	clearSelection: () => void;
	setCurrentResultId: (resultId: string | null) => void;
	isNodeOpen: (nodeKey: string) => boolean;
	setNodeOpen: (nodeKey: string, open: boolean) => void;
	setIsCanvasHovered: (hovered: boolean) => void;
	startEmbedDraw: () => void;
	seedEmbedArea: (bbox: BoundingBox, sourceObjectId: string) => void;
	setIsEmbedDrawActive: (active: boolean) => void;
	setEmbedBbox: (bbox: BoundingBox | null) => void;
	setEmbedFile: (file: File | null) => void;
	clearEmbedDraw: () => void;
}

export default class StructuredPromptStore implements IStructuredPromptStore {
	private currentResultId: string | null = null;
	private resultStates: Map<string, ResultState> = new Map();

	constructor() {
		makeAutoObservable(this);
	}

	private getOrCreateResultState(resultId: string): ResultState {
		if (!this.resultStates.has(resultId)) {
			this.resultStates.set(resultId, {
				hoveredObjectId: null,
				selectedObjectId: null,
				openNodes: new Set(),
				showAllObjects: !isFibo2App(),
				isCanvasHovered: false,
				isEmbedDrawActive: false,
				embedBbox: null,
				embedFile: null,
				embedSourceObjectId: null,
			});
		}
		return this.resultStates.get(resultId)!;
	}

	private getCurrentState(): ResultState | null {
		if (!this.currentResultId) return null;
		return this.getOrCreateResultState(this.currentResultId);
	}

	get hoveredObjectId(): string | null {
		return this.getCurrentState()?.hoveredObjectId ?? null;
	}

	get selectedObjectId(): string | null {
		return this.getCurrentState()?.selectedObjectId ?? null;
	}

	get showAllObjects(): boolean {
		return this.getCurrentState()?.showAllObjects ?? false;
	}

	get isCanvasHovered(): boolean {
		return this.getCurrentState()?.isCanvasHovered ?? false;
	}

	get isEmbedDrawActive(): boolean {
		return this.getCurrentState()?.isEmbedDrawActive ?? false;
	}

	get embedBbox(): BoundingBox | null {
		return this.getCurrentState()?.embedBbox ?? null;
	}

	get embedFile(): File | null {
		return this.getCurrentState()?.embedFile ?? null;
	}

	get embedSourceObjectId(): string | null {
		return this.getCurrentState()?.embedSourceObjectId ?? null;
	}

	setCurrentResultId = (resultId: string | null) => {
		this.currentResultId = resultId;
	};

	setHoveredObjectId = (id: string | null) => {
		const state = this.getCurrentState();
		if (state) {
			state.hoveredObjectId = id;
		}
	};

	setSelectedObjectId = (id: string | null) => {
		const state = this.getCurrentState();
		if (state) {
			state.selectedObjectId = id;
		}
	};

	setShowAllObjects = (show: boolean) => {
		const state = this.getCurrentState();
		if (state) {
			state.showAllObjects = show;
		}
	};

	setIsCanvasHovered = (hovered: boolean) => {
		const state = this.getCurrentState();
		if (state) {
			state.isCanvasHovered = hovered;
		}
	};

	toggleShowAllObjects = () => {
		const state = this.getCurrentState();
		if (state) {
			state.showAllObjects = !state.showAllObjects;
		}
	};

	/**
	 * Arms free-draw mode and drops any previous embed target. The selection goes with it:
	 * draw mode hides every bbox, so leaving one selected would strand the inspector.
	 */
	startEmbedDraw = () => {
		const state = this.getCurrentState();
		if (state) {
			state.isEmbedDrawActive = true;
			state.embedBbox = null;
			state.embedFile = null;
			state.embedSourceObjectId = null;
			state.selectedObjectId = null;
			state.hoveredObjectId = null;
		}
	};

	/**
	 * Creates an embed area already fitted to a detected object's box, skipping the drag.
	 *
	 * This is what makes "embed into this object" and "embed into a region I drew" the same
	 * single mechanism: the object only supplies the starting rectangle, which the user can
	 * then nudge like any other. The one thing downstream that tells them apart is
	 * `embedSourceObjectId`: the canvas hides the source object's dot, which would otherwise
	 * sit right under the embed area's "Add image" button.
	 */
	seedEmbedArea = (bbox: BoundingBox, sourceObjectId: string) => {
		const state = this.getCurrentState();
		if (state) {
			state.isEmbedDrawActive = false;
			state.embedBbox = { ...bbox };
			state.embedFile = null;
			state.embedSourceObjectId = sourceObjectId;
			state.selectedObjectId = null;
			state.hoveredObjectId = null;
		}
	};

	/** Ends (or re-arms) the drag phase without touching an already-committed area. */
	setIsEmbedDrawActive = (active: boolean) => {
		const state = this.getCurrentState();
		if (state) {
			state.isEmbedDrawActive = active;
		}
	};

	setEmbedBbox = (bbox: BoundingBox | null) => {
		const state = this.getCurrentState();
		if (state) {
			state.embedBbox = bbox;
		}
	};

	setEmbedFile = (file: File | null) => {
		const state = this.getCurrentState();
		if (state) {
			state.embedFile = file;
		}
	};

	clearEmbedDraw = () => {
		const state = this.getCurrentState();
		if (state) {
			state.isEmbedDrawActive = false;
			state.embedBbox = null;
			state.embedFile = null;
			state.embedSourceObjectId = null;
		}
	};

	clearSelection = () => {
		const state = this.getCurrentState();
		if (state) {
			state.hoveredObjectId = null;
			state.selectedObjectId = null;
		}
	};

	isNodeOpen = (nodeKey: string): boolean => {
		const state = this.getCurrentState();
		return state?.openNodes.has(nodeKey) ?? false;
	};

	setNodeOpen = (nodeKey: string, open: boolean) => {
		const state = this.getCurrentState();
		if (state) {
			if (open) {
				state.openNodes.add(nodeKey);
			} else {
				state.openNodes.delete(nodeKey);
			}
		}
	};
}
